import { useMemo } from 'react';

import { FieldStateKey, PermissionKey } from '@/common/constants';
import { useGetHiddenLevelAccess } from '@/modules/user/hooks';

const useGetHiddenFields = () => {
  const isDarkLargeLogoHidden = useGetHiddenLevelAccess(
    PermissionKey.DARK_LARGE_LOGO_PATH,
    FieldStateKey.DARK_LARGE_LOGO_PATH
  );
  const isDarkMinimizedLogoHidden = useGetHiddenLevelAccess(
    PermissionKey.DARK_MINIMIZED_LOGO_PATH,
    FieldStateKey.DARK_MINIMIZED_LOGO_PATH
  );
  const isFaviconLogoPathHidden = useGetHiddenLevelAccess(
    PermissionKey.FAVICON_LOGO_PATH,
    FieldStateKey.FAVICON_LOGO_PATH
  );
  const isLargeLogoHidden = useGetHiddenLevelAccess(
    PermissionKey.LARGE_LOGO_PATH,
    FieldStateKey.LARGE_LOGO_PATH
  );
  const isMinimizedLogoHidden = useGetHiddenLevelAccess(
    PermissionKey.MINIMIZED_LOGO_PATH,
    FieldStateKey.MINIMIZED_LOGO_PATH
  );

  const isMinimizedLogosHidden = useMemo(() => {
    return isMinimizedLogoHidden && isDarkMinimizedLogoHidden;
  }, [isDarkMinimizedLogoHidden, isMinimizedLogoHidden]);

  const isLargeLogosHidden = useMemo(() => {
    return isDarkLargeLogoHidden && isLargeLogoHidden;
  }, [isDarkLargeLogoHidden, isLargeLogoHidden]);

  const isBrandingHidden = useMemo(() => {
    return isMinimizedLogosHidden && isLargeLogosHidden && isFaviconLogoPathHidden;
  }, [isFaviconLogoPathHidden, isLargeLogosHidden, isMinimizedLogosHidden]);

  const isSourceTypeHidden = useGetHiddenLevelAccess(
    PermissionKey.ID_SOURCE_TYPE,
    FieldStateKey.ID_SOURCE_TYPE
  );

  const isEmployeeIdHidden = useMemo(() => {
    return isSourceTypeHidden;
  }, [isSourceTypeHidden]);

  //
  const isEmailLogoTypeHidden = useGetHiddenLevelAccess(
    PermissionKey.EMAIL_LOGO_TYPE,
    FieldStateKey.EMAIL_LOGO_TYPE
  );
  const isBrandingEmailIllustrationHidden = useGetHiddenLevelAccess(
    PermissionKey.EMAIL_ILLUSTRATION_ID,
    FieldStateKey.EMAIL_ILLUSTRATION_ID
  );

  const isEmailThemeHidden = useGetHiddenLevelAccess(
    PermissionKey.EMAIL_THEME_ID,
    FieldStateKey.EMAIL_THEME_ID
  );

  const isEmailConfiguratorHidden = useMemo(() => {
    return isEmailLogoTypeHidden && isBrandingEmailIllustrationHidden && isEmailThemeHidden;
  }, [isBrandingEmailIllustrationHidden, isEmailLogoTypeHidden, isEmailThemeHidden]);

  const iseThemeTypeHidden = useGetHiddenLevelAccess(PermissionKey.THEME_TYPE, FieldStateKey.THEME_TYPE);
  const isMobileCustomizationHidden = useMemo(() => iseThemeTypeHidden, [iseThemeTypeHidden]);

  const isEmailDomainHidden = useGetHiddenLevelAccess(PermissionKey.EMAIL_DOMAIN, FieldStateKey.EMAIL_DOMAIN);
  const isPrivacyPolicyHidden = useGetHiddenLevelAccess(
    PermissionKey.PRIVACY_POLICY_LINK,
    FieldStateKey.PRIVACY_POLICY_LINK
  );
  const isTermsAndConditionsHidden = useGetHiddenLevelAccess(
    PermissionKey.TERMS_AND_CONDITIONS_LINK,
    FieldStateKey.TERMS_AND_CONDITIONS_LINK
  );
  const isPricingConsentHidden = useGetHiddenLevelAccess(
    PermissionKey.PRICING_CONSENT,
    FieldStateKey.PRICING_CONSENT
  );
  const isAboutUsLinkHidden = useGetHiddenLevelAccess(
    PermissionKey.ABOUT_US_LINK,
    FieldStateKey.ABOUT_US_LINK
  );
  const isLinkManagementHidden = useMemo(() => {
    return (
      isEmailDomainHidden && isPrivacyPolicyHidden && isTermsAndConditionsHidden && isPricingConsentHidden
    );
  }, [isEmailDomainHidden, isPricingConsentHidden, isPrivacyPolicyHidden, isTermsAndConditionsHidden]);

  const isCustomServiceEmailHidden = useGetHiddenLevelAccess(
    PermissionKey.CUSTOMER_SERVICE_EMAIL,
    FieldStateKey.CUSTOMER_SERVICE_EMAIL
  );
  const isCustomServiceNumberHidden = useGetHiddenLevelAccess(
    PermissionKey.CUSTOMER_SERVICE_NUMBER,
    FieldStateKey.CUSTOMER_SERVICE_NUMBER
  );
  const isCustomerServiceURLHidden = useGetHiddenLevelAccess(
    PermissionKey.CUSTOMER_SERVICE_URL_LINK,
    FieldStateKey.CUSTOMER_SERVICE_URL_LINK
  );
  const isCustomerServiceURLNameHidden = useGetHiddenLevelAccess(
    PermissionKey.CUSTOMER_SERVICE_URL_NAME,
    FieldStateKey.CUSTOMER_SERVICE_URL_NAME
  );
  const isCustomEnablingHidden = useGetHiddenLevelAccess(
    PermissionKey.IS_CUSTOMER_SERVICE_ENABLED,
    FieldStateKey.IS_CUSTOMER_SERVICE_ENABLED
  );

  const isHelpSectionHidden = useMemo(() => {
    return (
      isCustomServiceEmailHidden &&
      isCustomServiceNumberHidden &&
      isCustomerServiceURLHidden &&
      isCustomerServiceURLNameHidden
    );
  }, [
    isCustomServiceEmailHidden,
    isCustomServiceNumberHidden,
    isCustomerServiceURLHidden,
    isCustomerServiceURLNameHidden,
  ]);

  const isCommunicationChannels = useGetHiddenLevelAccess(
    PermissionKey.COMMUNICATION_CHANNELS,
    FieldStateKey.COMMUNICATION_CHANNELS
  );

  const isCommunicationCategoriesHidden = useGetHiddenLevelAccess(
    PermissionKey.COMMUNICATION_CATEGORIES,
    FieldStateKey.COMMUNICATION_CATEGORIES
  );

  const isStatementFrequencyTypeHidden = useGetHiddenLevelAccess(
    PermissionKey.STATEMENT_FREQUENCY_TYPE,
    FieldStateKey.STATEMENT_FREQUENCY_TYPE
  );
  const isStatementTypeHidden = useGetHiddenLevelAccess(
    PermissionKey.STATEMENT_TYPE,
    FieldStateKey.STATEMENT_TYPE
  );

  const isCommunicationSectionHidden = useMemo(() => {
    return (
      isCommunicationChannels &&
      isCommunicationCategoriesHidden &&
      isStatementFrequencyTypeHidden &&
      isStatementTypeHidden
    );
  }, [
    isCommunicationCategoriesHidden,
    isCommunicationChannels,
    isStatementFrequencyTypeHidden,
    isStatementTypeHidden,
  ]);

  const isFileManagementAccessHidden = useGetHiddenLevelAccess(
    PermissionKey.IS_FILE_MANAGER_ACCESS_ENABLED,
    FieldStateKey.IS_FILE_MANAGER_ACCESS_ENABLED
  );

  const isFileAutoPostHidden = useGetHiddenLevelAccess(
    PermissionKey.IS_FILE_AUTO_POST_ENABLED,
    FieldStateKey.IS_FILE_AUTO_POST_ENABLED
  );
  const isFileLocationHidden = useGetHiddenLevelAccess(
    PermissionKey.FILE_LOCATION,
    FieldStateKey.FILE_LOCATION
  );

  const isFileManagerSectionHidden = useMemo(() => {
    return isFileManagementAccessHidden && isFileAutoPostHidden && isFileLocationHidden;
  }, [isFileAutoPostHidden, isFileLocationHidden, isFileManagementAccessHidden]);

  const isSessionTimeoutHidden = useGetHiddenLevelAccess(
    PermissionKey.SESSION_TIMEOUT_WARN,
    FieldStateKey.SESSION_TIMEOUT_WARN
  );
  const isSessionTimeoutLogoutHidden = useGetHiddenLevelAccess(
    PermissionKey.SESSION_TIMEOUT_LOGOUT,
    FieldStateKey.SESSION_TIMEOUT_LOGOUT
  );

  const isTechnicalSettingsSectionHidden = useMemo(() => {
    return isSessionTimeoutHidden && isSessionTimeoutLogoutHidden;
  }, [isSessionTimeoutHidden, isSessionTimeoutLogoutHidden]);


  const isClaimAssignmentNumberHidden = useGetHiddenLevelAccess(
    PermissionKey.CLAIMS_ASSIGNMENT_NUMBER,
    FieldStateKey.CLAIMS_ASSIGNMENT_NUMBER,
  );
  const isClaimsSectionsHidden = useMemo(() => {
    return isClaimAssignmentNumberHidden;
  }, [isClaimAssignmentNumberHidden]);

  const isCheckFundingModelHidden = useGetHiddenLevelAccess(
    PermissionKey.CHECK_FUNDING_MODEL,
    FieldStateKey.CHECK_FUNDING_MODEL,
  );
  const isElectronicFundingModeHidden = useGetHiddenLevelAccess(
    PermissionKey.ELECTRONIC_FUNDING_MODE,
    FieldStateKey.ELECTRONIC_FUNDING_MODE,
  );

  const isFundingModeSectionHidden = useMemo(() => {
    return isCheckFundingModelHidden && isElectronicFundingModeHidden;
  }, [isCheckFundingModelHidden, isElectronicFundingModeHidden]);


  const isInvoicingContactIdHidden = useGetHiddenLevelAccess(PermissionKey.INVOICING_CONTACT_ID);
  const isInvoicingDetailsSectionHidden = useMemo(() => {
    return isInvoicingContactIdHidden; 
  }, [isInvoicingContactIdHidden]);

  const isExternalPayrollContributionsAccountMethodHidden = useGetHiddenLevelAccess(
    PermissionKey.EXTERNAL_PAYROLL_CONTRIBUTIONS_ACCOUNT_METHOD,
    FieldStateKey.EXTERNAL_PAYROLL_CONTRIBUTIONS_ACCOUNT_METHOD,
  );
  const isPayrollContributionsHoldPeriodHidden = useGetHiddenLevelAccess(
    PermissionKey.PAYROLL_CONTRIBUTIONS_HOLD_PERIOD,
    FieldStateKey.PAYROLL_CONTRIBUTIONS_HOLD_PERIOD,
  );
  const isContributionPushGiveUpHidden = useGetHiddenLevelAccess(
    PermissionKey.CONTRIBUTION_PUSH_GIVE_UP_DAYS,
    FieldStateKey.CONTRIBUTION_PUSH_GIVE_UP_DAYS,
  );
  const isPayrollContributionSectionHidden = useMemo(() => {
    return isExternalPayrollContributionsAccountMethodHidden && isPayrollContributionsHoldPeriodHidden && isContributionPushGiveUpHidden;
  }, [isContributionPushGiveUpHidden, isExternalPayrollContributionsAccountMethodHidden, isPayrollContributionsHoldPeriodHidden]);



  const isEEContributionHoldPeriodHidden = useGetHiddenLevelAccess(
    PermissionKey.EE_CONTRIBUTIONS_HOLD_PERIOD,
    FieldStateKey.EE_CONTRIBUTIONS_HOLD_PERIOD,
  );

  const isIndividualContributionSectionHidden = useMemo(() => {
    return isEEContributionHoldPeriodHidden;
  }, [isEEContributionHoldPeriodHidden]);


  const isALlowManualContributionHidden = useGetHiddenLevelAccess(
    PermissionKey.ALLOW_MANUAL_CONTRIBUTION,
    FieldStateKey.ALLOW_MANUAL_CONTRIBUTION
  );
  const isAllowScheduledContributionHidden = useGetHiddenLevelAccess(
    PermissionKey.ALLOW_SCHEDULED_CONTRIBUTION,
    FieldStateKey.ALLOW_SCHEDULED_CONTRIBUTION
  );
  const isAdminContributionsSectionHidden = useMemo(() => {
    return isALlowManualContributionHidden && isALlowManualContributionHidden;
  },[isALlowManualContributionHidden]);


  const isReplenishmentDayNumberHidden = useGetHiddenLevelAccess(
    PermissionKey.REPLENISHMENT_DAY_NUMBER,
    FieldStateKey.REPLENISHMENT_DAY_NUMBER,
  );
  const isReplenishmentFrequencyTypeHidden = useGetHiddenLevelAccess(
    PermissionKey.REPLENISHMENT_FREQUENCY_TYPE,
    FieldStateKey.REPLENISHMENT_FREQUENCY_TYPE,
  );
  const isExternalReplenishmentAccountMethodHidden = useGetHiddenLevelAccess(
    PermissionKey.EXTERNAL_REPLENISHMENT_ACCOUNT_METHOD,
    FieldStateKey.EXTERNAL_REPLENISHMENT_ACCOUNT_METHOD,
  );

  const isFundingReplenishmentSectionHidden = useMemo(() => {
    return isReplenishmentDayNumberHidden && isReplenishmentFrequencyTypeHidden && isExternalReplenishmentAccountMethodHidden;
  }, [isExternalReplenishmentAccountMethodHidden, isReplenishmentDayNumberHidden, isReplenishmentFrequencyTypeHidden]);



  const isPrefundReplenishmentCreepMinHidden = useGetHiddenLevelAccess(
    PermissionKey.PREFUND_REPLENISHMENT_CREEP_MIN,
    FieldStateKey.PREFUND_REPLENISHMENT_CREEP_MIN,
  );
  const isPrefundReplenishmentCreepRecentHidden = useGetHiddenLevelAccess(
    PermissionKey.PREFUND_REPLENISHMENT_CREEP_PERCENT,
    FieldStateKey.PREFUND_REPLENISHMENT_CREEP_PERCENT,
  );
  const isPrefundReplenishmentMinValueHidden = useGetHiddenLevelAccess(
    PermissionKey.PREFUND_REPLENISHMENT_MIN_VALUE,
    FieldStateKey.PREFUND_REPLENISHMENT_MIN_VALUE,
  );
  const isPrefundReplenishmentPercentHidden = useGetHiddenLevelAccess(
    PermissionKey.PREFUND_REPLENISHMENT_PERCENT,
    FieldStateKey.PREFUND_REPLENISHMENT_PERCENT,
  );
  const isAllowPrefundHidden = useGetHiddenLevelAccess(
    PermissionKey.ALLOW_PREFUND,
    FieldStateKey.ALLOW_PREFUND,
  );

  const isPrefundSectionHidden = useMemo(() => {
    return isPrefundReplenishmentCreepMinHidden && isPrefundReplenishmentCreepRecentHidden && isPrefundReplenishmentMinValueHidden && isPrefundReplenishmentPercentHidden && isAllowPrefundHidden;
  }, [isAllowPrefundHidden, isPrefundReplenishmentCreepMinHidden, isPrefundReplenishmentCreepRecentHidden, isPrefundReplenishmentMinValueHidden, isPrefundReplenishmentPercentHidden]);

  return {
    isBrandingHidden,
    isLargeLogosHidden,
    isMinimizedLogoHidden,
    isLargeLogoHidden,
    isFaviconLogoPathHidden,
    isDarkMinimizedLogoHidden,
    isDarkLargeLogoHidden,
    isMinimizedLogosHidden,
    isSourceTypeHidden,
    isEmployeeIdHidden,

    isEmailLogoTypeHidden,
    isBrandingEmailIllustrationHidden,
    isEmailThemeHidden,
    isEmailConfiguratorHidden,

    iseThemeTypeHidden,
    isMobileCustomizationHidden,

    isEmailDomainHidden,
    isPrivacyPolicyHidden,
    isAboutUsLinkHidden,
    isPricingConsentHidden,
    isTermsAndConditionsHidden,
    isLinkManagementHidden,

    isCustomServiceEmailHidden,
    isCustomServiceNumberHidden,
    isCustomerServiceURLHidden,
    isCustomerServiceURLNameHidden,
    isCustomEnablingHidden,
    isHelpSectionHidden,

    isCommunicationChannels,
    isCommunicationCategoriesHidden,
    isStatementFrequencyTypeHidden,
    isStatementTypeHidden,
    isCommunicationSectionHidden,

    isFileManagementAccessHidden,
    isFileAutoPostHidden,
    isFileLocationHidden,
    isFileManagerSectionHidden,

    isSessionTimeoutLogoutHidden,
    isSessionTimeoutHidden,
    isTechnicalSettingsSectionHidden,

    isClaimAssignmentNumberHidden,
    isClaimsSectionsHidden,

    isElectronicFundingModeHidden,
    isCheckFundingModelHidden,
    isFundingModeSectionHidden,
    
    isInvoicingContactIdHidden,
    isInvoicingDetailsSectionHidden,

    isExternalPayrollContributionsAccountMethodHidden,
    isPayrollContributionsHoldPeriodHidden,
    isContributionPushGiveUpHidden,
    isPayrollContributionSectionHidden,

    isEEContributionHoldPeriodHidden,
    isIndividualContributionSectionHidden,

    isALlowManualContributionHidden,
    isAllowScheduledContributionHidden,
    isAdminContributionsSectionHidden,

    isReplenishmentDayNumberHidden,
    isReplenishmentFrequencyTypeHidden,
    isExternalReplenishmentAccountMethodHidden,
    isFundingReplenishmentSectionHidden ,


    isPrefundReplenishmentCreepMinHidden,
    isPrefundReplenishmentCreepRecentHidden,
    isPrefundReplenishmentMinValueHidden,
    isPrefundReplenishmentPercentHidden,
    isAllowPrefundHidden,
    isPrefundSectionHidden
  };
};

export default useGetHiddenFields;
